var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { FieldTitle, InputHelperText, sanitizeInputRestProps, useInput, useLocaleState, useNotify, useResourceContext, } from 'react-admin';
import { TextField, useFormControl, styled } from '@mui/material';
import { useWatch } from 'react-hook-form';
import clsx from 'clsx';
import { singularize } from 'inflection';
import { useGetCompletion, } from '../../../dataProvider';
/**
 * A TextareaInput that suggests completions when the user types.
 *
 * Warning: This input updates the form value onBlur, not on every keystroke.
 */
// eslint-disable-next-line prettier/prettier
export var TextareaWithCompletion = function (props) {
    var _a;
    var className = props.className, _b = props.debounce, debounce = _b === void 0 ? 1000 : _b, defaultValue = props.defaultValue, format = props.format, fullWidth = props.fullWidth, helperText = props.helperText, label = props.label, locale = props.locale, maxRows = props.maxRows, maxSize = props.maxSize, meta = props.meta, minRows = props.minRows, parse = props.parse, _c = props.promptGenerator, promptGenerator = _c === void 0 ? defaultPromptGenerator : _c, queryOptions = props.queryOptions, _d = props.rows, rows = _d === void 0 ? maxRows : _d, source = props.source, stop = props.stop, sx = props.sx, temperature = props.temperature, validate = props.validate, variant = props.variant, rest = __rest(props, ["className", "debounce", "defaultValue", "format", "fullWidth", "helperText", "label", "locale", "maxRows", "maxSize", "meta", "minRows", "parse", "promptGenerator", "queryOptions", "rows", "source", "stop", "sx", "temperature", "validate", "variant"]);
    var resource = useResourceContext(props);
    if (!resource) {
        throw new Error('The TextareaWithCompletion component must be used inside a ResourceContext or must be passed a resource prop.');
    }
    var _e = useInput({
        defaultValue: defaultValue,
        format: format,
        parse: parse,
        resource: resource,
        source: source,
        validate: validate,
    }), field = _e.field, id = _e.id, isRequired = _e.isRequired, _f = _e.fieldState, error = _f.error, invalid = _f.invalid, isTouched = _f.isTouched, isSubmitted = _e.formState.isSubmitted;
    var renderHelperText = helperText !== false || ((isTouched || isSubmitted) && invalid);
    // the ref for the contentEditable div
    var inputRef = React.useRef(null);
    // local copy of the contenteditable value, used for the label shrink
    var _g = React.useState(field.value), content = _g[0], setContent = _g[1];
    // the initial content of the contenteditable
    var _h = React.useState(field.value), contentEditableInitialValue = _h[0], setContentEditableInitialValue = _h[1];
    // the content before completion is used to replace the content if the user dismisses the completion
    var contentBeforeCompletion = React.useRef('');
    // To detect when the user expects a completion, we must follow its position in the textarea
    var _j = React.useState(0), caretPosition = _j[0], setCaretPosition = _j[1];
    // To force a completion on focus, we track the focused state
    var _k = React.useState(false), isFocused = _k[0], setIsFocused = _k[1];
    var _l = React.useState(false), readyForCompletion = _l[0], setReadyForCompletion = _l[1];
    // we need to keep the latest suggested completion, so add it to the content it the user accepts it
    var completion = React.useRef('');
    var updateContent = function (text) {
        setContent(text);
        field.onChange({ target: { value: text } });
    };
    // if the form value has changed from outside, update the value on screen
    React.useEffect(function () {
        // Content and field.value are normally synchronized in this component (see updateContent above).
        // If they are ever out of sync, it's because the field value has changed from outside.
        if (content !== field.value) {
            // In that case, we reset the contenteditable value to match the field value.
            setContentEditableInitialValue(field.value);
        }
        // Otherwise we don't update it, or the cursor would move to the beginning of the field.
        // The contenteditable already displays the right content (the content modified by the user).
    }, [content, field.value]);
    var handleMove = function () {
        if (!inputRef.current) {
            return;
        }
        setReadyForCompletion(false);
        var input = inputRef.current;
        if (!elementContainsSelection(input)) {
            return;
        }
        var newCaretPosition = getCaretPosition(input);
        if (newCaretPosition.start != null &&
            newCaretPosition.start === newCaretPosition.end) {
            setCaretPosition(newCaretPosition.start);
        }
    };
    var handleType = function (e) {
        var _a;
        if (!inputRef.current) {
            return;
        }
        setReadyForCompletion(false);
        var input = inputRef.current;
        // if user pressed tab and there is an active completion, change the content
        if (e.key === 'Tab' && completion.current) {
            e.preventDefault();
            input.textContent =
                contentBeforeCompletion.current.slice(0, caretPosition) +
                    completion.current +
                    contentBeforeCompletion.current.slice(caretPosition);
            updateContent(input.textContent);
            // Move the caret after the completion
            moveCaret(input, caretPosition + completion.current.length);
            // reset the completion
            completion.current = '';
        }
        else {
            // user didn't press tab
            if (completion.current) {
                // user dismissed the completion: empty it and remove it from the content
                completion.current = '';
                input.textContent = contentBeforeCompletion.current;
                // Move the caret to the recorded caretPosition
                moveCaret(input, caretPosition);
            }
            if (e.key === 'Enter') {
                // Browsers add a <div> after the <br> when pressing enter,
                // so we add the element manually instead.
                if (navigator.userAgent.toLowerCase().includes('firefox')) {
                    // FIXME: insertLineBreak messes the caret position in Firefox
                    // so we must find another way
                }
                else {
                    // execCommand is deprecated but there is no alternative and it's supported everywhere
                    document.execCommand('insertLineBreak');
                    e.preventDefault();
                }
            }
        }
        contentBeforeCompletion.current = (_a = input.textContent) !== null && _a !== void 0 ? _a : '';
    };
    var handleFocus = function () {
        // used to trigger the completion when the user clicks in the field
        setIsFocused(true);
    };
    var handleBlur = function (e) {
        if (!inputRef.current)
            return;
        setReadyForCompletion(false);
        if (completion.current) {
            completion.current = '';
            inputRef.current.textContent = contentBeforeCompletion.current;
        }
        // @ts-ignore the onBlur signature expects no event, which is a mistake
        field.onBlur(e);
        setContentEditableInitialValue(inputRef.current.textContent);
        setIsFocused(false);
    };
    // Handle paste to avoid pasting HTML
    // @see https://developer.mozilla.org/en-US/docs/Web/API/Element/paste_event#examples
    var handlePaste = function (e) {
        e.preventDefault();
        var clipboardText = // @ts-ignore
         (event.clipboardData || window.clipboardData).getData('text');
        var selection = window.getSelection();
        if (!selection || !selection.rangeCount)
            return;
        selection.deleteFromDocument();
        selection
            .getRangeAt(0)
            .insertNode(document.createTextNode(clipboardText));
        selection.collapseToEnd();
    };
    // Determine when the input is ready for completion:
    // - only if the caret is at the end of a line,
    // - and if one second has passed without any change to the content)
    React.useEffect(function () {
        if (!inputRef.current)
            return;
        var input = inputRef.current;
        var timer = setTimeout(function () {
            var textContent = input.innerText;
            if (document.activeElement === input &&
                (textContent === '' ||
                    (textContent === null || textContent === void 0 ? void 0 : textContent[caretPosition]) === '\n' ||
                    caretPosition === (textContent === null || textContent === void 0 ? void 0 : textContent.length))) {
                setReadyForCompletion(true);
            }
            else {
                setReadyForCompletion(false);
            }
        }, debounce);
        return function () {
            clearTimeout(timer);
        };
    }, [caretPosition, debounce, isFocused]);
    var record = useWatch();
    var localeFromI18n = useLocaleState()[0];
    var contentLocale = locale || localeFromI18n;
    var notify = useNotify();
    // When ready for completion, use the AI backend to complete the text
    var onSuccess = function (_a) {
        var data = _a.data;
        var input = inputRef.current;
        if (!input)
            return;
        if (!data)
            return;
        if (!readyForCompletion)
            return;
        var textContent = input.innerText;
        contentBeforeCompletion.current = textContent;
        var cleanedUpCompletion = cleanupCompletion(data);
        completion.current = cleanedUpCompletion;
        setReadyForCompletion(false);
        // Add the completion to the content, at the caret position
        // we switch to HTMLContent instead of textContent
        // to be able to style the completion
        var newHtmlContent = textContent.slice(0, caretPosition) +
            "<span style=\"opacity:0.5\">".concat(cleanedUpCompletion, "</span>") +
            textContent.slice(caretPosition);
        input.innerHTML = newHtmlContent;
        // Move the caret before the completion
        moveCaret(input, caretPosition);
    };
    var onError = function (error) {
        if (!readyForCompletion)
            return;
        completion.current = '';
        notify((error === null || error === void 0 ? void 0 : error.message) || 'ra-ai.notification.getCompletion.error', {
            type: 'error',
            messageArgs: {
                _: (error === null || error === void 0 ? void 0 : error.message) || 'Error fetching completion',
            },
        });
    };
    useGetCompletion({
        prompt: promptGenerator({
            resource: resource,
            name: source,
            record: record,
            value: (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.innerText,
            caretPosition: caretPosition,
            locale: contentLocale,
        }),
        stop: stop,
        temperature: temperature,
        maxSize: maxSize,
        meta: meta,
    }, __assign({ enabled: readyForCompletion, onSuccess: onSuccess, onError: onError }, queryOptions));
    return (React.createElement(StyledTextField, __assign({ variant: variant, fullWidth: fullWidth, multiline: true, className: clsx('ra-input', "ra-input-".concat(source), className), sx: __assign({ '& .MuiInputBase-input': {
                width: function (theme) {
                    var _a, _b, _c;
                    return fullWidth == null
                        ? ((_c = (_b = (_a = theme.components) === null || _a === void 0 ? void 0 : _a.MuiTextField) === null || _b === void 0 ? void 0 : _b.defaultProps) === null || _c === void 0 ? void 0 : _c.fullWidth)
                            ? '100%'
                            : undefined
                        : fullWidth
                            ? '100%'
                            : undefined;
                },
                minHeight: function (theme) {
                    return minRows
                        ? "calc(".concat(minRows, " * ").concat(theme.typography.body1.lineHeight, " * 1em)")
                        : undefined;
                },
                maxHeight: function (theme) {
                    return rows
                        ? "calc(".concat(rows, " * ").concat(theme.typography.body1.lineHeight, " * 1em)")
                        : undefined;
                },
                overflowY: rows ? 'auto' : undefined,
            } }, sx), label: label !== '' && label !== false ? (React.createElement(FieldTitle, { label: label, source: source, resource: resource, isRequired: isRequired })) : null, id: id, InputLabelProps: { shrink: !!content || undefined }, InputProps: {
            slots: { input: ContentEditableDiv },
            slotProps: {
                input: {
                    onKeyDown: handleType,
                    onMouseDown: handleType,
                    onKeyUp: handleMove,
                    onMouseUp: handleMove,
                    onInput: function (e) {
                        return updateContent(e.target.innerText);
                    },
                    onPaste: handlePaste,
                    dangerouslySetInnerHTML: {
                        __html: contentEditableInitialValue,
                    },
                    // @ts-ignore custom property
                    // @see ContentEditableDiv
                    isFilled: contentEditableInitialValue != '',
                },
            },
        }, error: (isTouched || isSubmitted) && invalid, helperText: renderHelperText ? (React.createElement(InputHelperText, { error: error === null || error === void 0 ? void 0 : error.message, helperText: helperText })) : null, inputRef: inputRef, onBlur: handleBlur, onFocus: handleFocus }, sanitizeInputRestProps(rest))));
};
var StyledTextField = styled(TextField, {
    name: 'RaAiTextareaWithCompletion',
    overridesResolver: function (props, styles) { return styles.root; },
})({
    '& .MuiInputBase-input': {
        minWidth: 190,
        whiteSpace: 'pre-wrap',
        padding: 0,
        paddingTop: 0,
        WebkitTapHighlightColor: 'transparent',
        '&:focus': {
            outline: 0,
        },
    },
});
/**
 * A <div contentEditable> that can be used as a slot for MUI's TextField.
 */
var ContentEditableDiv = React.forwardRef(function ContentEditableDiv(_a, ref) {
    var isFilled = _a.isFilled, minRows = _a.minRows, maxRows = _a.maxRows, ownerState = _a.ownerState, props = __rest(_a, ["isFilled", "minRows", "maxRows", "ownerState"]);
    // MUI's InputBase uses a custom context to tell the label
    // whether the input is filled or not so it can shrink.
    // As we don't use InputBase, we have to replicate this context
    var muiFormControl = useFormControl();
    var onFilled = muiFormControl && muiFormControl.onFilled;
    var onEmpty = muiFormControl && muiFormControl.onEmpty;
    React.useEffect(function () {
        if (isFilled) {
            onFilled && onFilled();
        }
        else {
            onEmpty && onEmpty();
        }
    }, [isFilled, onFilled, onEmpty]);
    return (React.createElement("div", __assign({ ref: ref, role: "textbox", tabIndex: 0, contentEditable: true, className: "'text-editable" }, props)));
});
var defaultPromptGenerator = function (_a) {
    var resource = _a.resource, name = _a.name, value = _a.value, _b = _a.record, record = _b === void 0 ? {} : _b, locale = _a.locale, caretPosition = _a.caretPosition;
    var cleanedRecord = Object.keys(record).reduce(function (acc, key) {
        if (key !== name && record[key]) {
            acc[key] = record[key];
        }
        return acc;
    }, {});
    var recordRepresentation = Object.keys(cleanedRecord)
        .map(function (key) { return "".concat(key, ": ").concat(cleanedRecord[key]); })
        .join('\n');
    var resourceName = resource ? singularize(resource) : 'record';
    if (!value) {
        return "The following describes one ".concat(resourceName, ", using the ").concat(locale, " locale:\n").concat(recordRepresentation, "\n").concat(name, ": ");
    }
    else {
        var prompt_1 = "\nYou will be provided with an incomplete ".concat(resourceName, " delimited by triple quotes. It contains a {{REDACTED}} portion that you must complete using the ").concat(locale, " locale.\n\n\"\"\"\n").concat(recordRepresentation, "\n").concat(name, ": ").concat(value.slice(0, caretPosition), "{{REDACTED}}").concat(value.slice(caretPosition), "\n\"\"\"\n\nWrite only the {{REDACTED}} portion, without the surrounding content.\n\n{{REDACTED}}:");
        return prompt_1;
    }
};
/**
 * Get the cleaned up completion, without the surrounding newlines and double quotes
 * @example cleanupCompletion(`  "lorem ipsum"`) === "lorem ipsum"
 */
var cleanupCompletion = function (text) {
    return text
        .trim()
        .replace(/^"(.*)"$/, '$1')
        .replace(/^'(.*)'$/, '$1')
        .replace('{{REDACTED}}', '')
        .replace(/^\:(.*)$/, '$1')
        .trim();
};
var moveCaret = function (element, position) {
    if (!(element.childNodes[0] instanceof Node)) {
        return;
    }
    var coord = getCoordinatesForPosition(element, position);
    var selection = window.getSelection();
    if (!selection)
        return;
    selection.removeAllRanges();
    var range = new Range();
    range.setStart(element.childNodes[coord.line], coord.column);
    range.collapse(true);
    selection.addRange(range);
};
function getCoordinatesForPosition(element, position) {
    var counter = position;
    var line = 0;
    var column = 0;
    var finished = false;
    element.childNodes.forEach(function (node) {
        var _a, _b;
        if (finished) {
            return;
        }
        if (node instanceof Node) {
            if (node.nodeName === 'BR') {
                line++;
                if (counter > 0) {
                    counter--;
                }
                column = 0;
            }
            else if (node.nodeName === '#text') {
                var textLength = (_b = (_a = node.nodeValue) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
                if (counter <= textLength) {
                    column = counter;
                    finished = true;
                }
                else {
                    counter -= textLength;
                }
            }
        }
    });
    return { line: line, column: column };
}
// taken from https://stackoverflow.com/questions/8339857/how-to-know-if-selected-text-is-inside-a-specific-div
var elementContainsSelection = function (el) {
    var selection = window.getSelection();
    if (!selection)
        return false;
    if (selection.rangeCount > 0) {
        for (var i = 0; i < selection.rangeCount; ++i) {
            if (!el.contains(selection.getRangeAt(i).commonAncestorContainer)) {
                return false;
            }
        }
        return true;
    }
    return false;
};
function getCaretPosition(element) {
    var selection = window.getSelection();
    if (!selection)
        return { start: 0, end: 0 };
    var range = selection.getRangeAt(0);
    var preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    var end = countCharactersIncludingNewlines(preCaretRange);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    var start = countCharactersIncludingNewlines(preCaretRange);
    return { start: start, end: end };
}
function countCharactersIncludingNewlines(range) {
    var divs = range.cloneContents().querySelectorAll('div');
    var count = range.toString().length;
    divs.forEach(function () { return count++; }); // add 1 for each newline (div)
    return count;
}
